<script lang="ts">
  import { tStore } from '../../../i18n'
  import Tooltip from '../../../components/Tooltip.svelte'
  import CpvCombobox from '../../../components/CpvCombobox.svelte'
  import CountrySelect from '../../../components/CountrySelect.svelte'
  import Dropdown from '../../../components/Dropdown.svelte'
  import type {
    ProcurementFilters,
    ProcurementType,
    ValueRangeCurrency,
  } from '../../../api/procurements'
  import type { Source } from '../../../api/sources'
  import type { SavedFilter } from '../../../api/savedFilters'
  import { isFilterModified } from '../../../composables/savedFilterDiff'
  import StatusBadge from '../../../components/StatusBadge.svelte'
  import { VALUE_RANGE_PRESETS } from './constants'

  const typeOptions: ProcurementType[] = ['simple', 'public']
  // 'ANY' is component-local (never sent to the backend — see setCurrency)
  // and represents "don't scope the value range to one currency".
  type CurrencyChoice = ValueRangeCurrency | 'ANY'

  let {
    filters,
    valueRangeChoice = $bindable(),
    activeSavedFilterId,
    sources,
    savedFilters,
    open,
    bulkMode,
    selectedCount,
    bulkSelectionLimit,
    occupiedBulkSlots,
    hasBulkResults,
    sourceLocked,
    onApply,
    onClear,
    onToggle,
    onSelectSavedFilter,
    onToggleBulkMode,
    onRunBulkAi,
    onViewBulkResults,
    onDeselectSource,
  }: {
    filters: ProcurementFilters
    valueRangeChoice: string
    activeSavedFilterId: number | undefined
    sources: Source[]
    savedFilters: SavedFilter[]
    open: boolean
    bulkMode: boolean
    selectedCount: number
    bulkSelectionLimit: number
    occupiedBulkSlots: number
    hasBulkResults: boolean
    // True while the source filter was set by a Top Sources click rather
    // than picked here — the dropdown locks to it until deselected.
    sourceLocked: boolean
    onApply: () => void
    onClear: () => void
    onToggle: () => void
    onSelectSavedFilter: (id: number | undefined) => void
    onToggleBulkMode: () => void
    onRunBulkAi: () => void
    onViewBulkResults: () => void
    onDeselectSource: () => void
  } = $props()

  // 'custom' (labeled "Any value" in the dropdown) is the only non-preset
  // option now — picking it just reveals the Min/Max inputs without
  // touching whatever is already in them; leaving both empty there means
  // no restriction, same as literally "any value".
  function applyValueRangePreset(choice: string) {
    valueRangeChoice = choice
    if (choice === 'custom') return
    const preset = VALUE_RANGE_PRESETS.find((p) => p.id === choice)
    if (!preset) return
    filters.minValueEur = preset.min ?? undefined
    filters.maxValueEur = preset.max ?? undefined
  }

  // 'ANY' clears filters.currency entirely — the request then compares
  // COALESCE(value_eur, value_original) instead of scoping to one
  // currency's column (see ProcurementRepository.applyFilters). Switching
  // currency also resets the value bound: a min/max typed under one
  // currency reinterpreted under another (or carried into "Any currency")
  // would silently keep narrowing results with numbers that no longer mean
  // what the user set them for — and since there's no FX conversion, the
  // same figures aren't meaningfully comparable across currencies anyway.
  function setCurrency(next: CurrencyChoice) {
    filters.currency = next === 'ANY' ? undefined : next
    filters.minValueEur = undefined
    filters.maxValueEur = undefined
    valueRangeChoice = 'custom'
  }

  function formatBound(amount: number): string {
    return filters.currency === 'CHF'
      ? `CHF ${amount.toLocaleString('de-CH')}`
      : `€${amount.toLocaleString('de-DE')}`
  }

  // True once the draft filter fields no longer match the selected saved
  // filter's definition — drives the "Modified" badge next to the field
  // label below. `undefined` while nothing is selected (or the selected id
  // no longer resolves to a saved filter).
  const isActiveSavedFilterModified = $derived.by(() => {
    const active = savedFilters.find((sf) => sf.id === activeSavedFilterId)
    return active !== undefined && isFilterModified(filters, active.filterConfig)
  })
  const savedFilterOptions = $derived([
    {
      value: undefined,
      label:
        savedFilters.length === 0
          ? $tStore('views.procurements.filters.savedFilterEmpty')
          : $tStore('views.procurements.filters.savedFilterNone'),
    },
    ...savedFilters.map((sf) => ({ value: sf.id, label: sf.name })),
  ])
  const sourceOptions = $derived([
    { value: undefined, label: $tStore('common.all') },
    ...sources.map((src) => ({ value: src.id, label: src.displayName })),
  ])
  const typeDropdownOptions = $derived([
    { value: undefined, label: $tStore('common.all') },
    ...typeOptions.map((opt) => ({ value: opt, label: $tStore(`views.procurements.type.${opt}`) })),
  ])
  const currencyOptions = $derived<{ value: CurrencyChoice; label: string }[]>([
    { value: 'ANY', label: $tStore('views.procurements.filters.currencyAny') },
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'CHF', label: 'CHF (CHF)' },
  ])
  const valueRangeOptions = $derived([
    ...VALUE_RANGE_PRESETS.map((preset) => ({
      value: preset.id,
      label:
        preset.max === null
          ? `${formatBound(preset.min ?? 0)}+`
          : `${formatBound(preset.min ?? 0)} – ${formatBound(preset.max)}`,
    })),
    { value: 'custom', label: $tStore('views.procurements.filters.valueRangeAny') },
  ])
</script>

<div class="filter-panel">
  <div class="filter-panel__head">
    <h3>{$tStore('views.dashboard.allProcurements')}</h3>
    <div class="filter-panel__head-actions">
      <Tooltip
        text={bulkMode
          ? $tStore('views.dashboard.exitBulkAiAnalysisTooltip')
          : $tStore('views.dashboard.bulkAiAnalysisTooltip', { max: bulkSelectionLimit })}
      >
        <button
          type="button"
          class="btn btn--secondary btn--sm bulk-toggle"
          class:btn--active-danger={bulkMode}
          onclick={onToggleBulkMode}
          aria-pressed={bulkMode}
        >
          {$tStore(
            bulkMode ? 'views.dashboard.exitBulkAiAnalysis' : 'views.dashboard.bulkAiAnalysis',
          )}
        </button>
      </Tooltip>
      {#if bulkMode}
        <button
          type="button"
          class="btn btn--primary btn--sm run-bulk-ai"
          onclick={onRunBulkAi}
          disabled={selectedCount === 0}
          title={$tStore('views.dashboard.runBulkAiTooltip')}
        >
          {$tStore('views.dashboard.runBulkAi')}{selectedCount > 0 ? ` (${selectedCount})` : ''}
        </button>
        {#if hasBulkResults}
          <button type="button" class="bulk-slots bulk-slots--link" onclick={onViewBulkResults}>
            {$tStore('views.dashboard.bulkSlotsInUse', {
              occupied: occupiedBulkSlots,
              max: bulkSelectionLimit,
            })}
          </button>
        {:else}
          <span class="bulk-slots">
            {$tStore('views.dashboard.bulkSlotsInUse', {
              occupied: occupiedBulkSlots,
              max: bulkSelectionLimit,
            })}
          </span>
        {/if}
      {/if}
      <button
        type="button"
        class="btn btn--secondary btn--sm filters__toggle"
        onclick={onToggle}
        aria-expanded={open}
        aria-controls="dashboard-filters"
      >
        {open ? $tStore('views.dashboard.hideFilters') : $tStore('views.dashboard.showFilters')}
        <span class="filters__toggle-icon" aria-hidden="true">{open ? '▴' : '▾'}</span>
      </button>
    </div>
  </div>

  {#if open}
    <form
      id="dashboard-filters"
      class="filters"
      onsubmit={(e) => {
        e.preventDefault()
        onApply()
      }}
    >
      <label class="field filters__saved">
        <span class="filters__saved-label">
          <span class="field__label">{$tStore('views.procurements.filters.savedFilter')}</span>
          {#if isActiveSavedFilterModified}
            <StatusBadge
              label={$tStore('views.procurements.filters.savedFilterModified')}
              tone="warning"
            />
          {/if}
        </span>
        {#if sourceLocked}
          <Tooltip text={$tStore('views.procurements.filters.savedFilterLockedTooltip')}>
            <Dropdown value={activeSavedFilterId} options={savedFilterOptions} disabled />
          </Tooltip>
        {:else}
          <Dropdown
            value={activeSavedFilterId}
            options={savedFilterOptions}
            disabled={savedFilters.length === 0}
            onchange={onSelectSavedFilter}
          />
        {/if}
      </label>

      <label class="field filters__search">
        <span class="field__label">{$tStore('views.procurements.filters.search')}</span>
        <input class="input" type="text" bind:value={filters.search} />
      </label>

      <label class="field">
        <span class="field__label">{$tStore('views.procurements.filters.source')}</span>
        {#if sourceLocked}
          <div class="field__locked-source">
            <Tooltip text={$tStore('views.procurements.filters.sourceLockedTooltip')}>
              <Dropdown value={filters.sourceId} options={sourceOptions} disabled />
            </Tooltip>
            <Tooltip text={$tStore('views.dashboard.topSourcesDeselectTooltip')}>
              <button
                type="button"
                class="field__clear-source"
                onclick={onDeselectSource}
                aria-label={$tStore('views.dashboard.topSourcesDeselect')}
              >
                ✕
              </button>
            </Tooltip>
          </div>
        {:else}
          <Dropdown bind:value={filters.sourceId} options={sourceOptions} />
        {/if}
      </label>

      <label class="field">
        <span class="field__label">{$tStore('views.procurements.filters.country')}</span>
        <CountrySelect bind:value={filters.country} />
      </label>

      <label class="field">
        <span class="field__label">{$tStore('views.procurements.filters.type')}</span>
        <Dropdown bind:value={filters.procurementType} options={typeDropdownOptions} />
      </label>

      <label class="field">
        <span class="field__label">{$tStore('views.procurements.filters.cpv')}</span>
        <CpvCombobox bind:value={filters.cpv} placeholder="e.g. 72" />
      </label>

      <label class="field">
        <span class="field__label">{$tStore('views.procurements.filters.minScore')}</span>
        <input
          class="input"
          type="number"
          min="0"
          max="100"
          step="1"
          placeholder="0–100"
          bind:value={filters.minAiScore}
        />
      </label>

      <label class="field">
        <span class="field__label">{$tStore('views.procurements.filters.currency')}</span>
        <Dropdown
          value={filters.currency ?? 'ANY'}
          options={currencyOptions}
          onchange={setCurrency}
        />
      </label>

      {#if filters.currency}
        <label class="field">
          <span class="field__label">{$tStore('views.procurements.filters.valueRange')}</span>
          <Dropdown
            value={valueRangeChoice}
            options={valueRangeOptions}
            onchange={applyValueRangePreset}
          />
        </label>

        {#if valueRangeChoice === 'custom'}
          <label class="field">
            <span class="field__label">
              {$tStore('views.procurements.filters.minValue', {
                currency: filters.currency === 'CHF' ? 'CHF' : '€',
              })}
            </span>
            <input
              class="input"
              type="number"
              min="0"
              step="any"
              bind:value={filters.minValueEur}
            />
          </label>
          <label class="field">
            <span class="field__label">
              {$tStore('views.procurements.filters.maxValue', {
                currency: filters.currency === 'CHF' ? 'CHF' : '€',
              })}
            </span>
            <input
              class="input"
              type="number"
              min="0"
              step="any"
              bind:value={filters.maxValueEur}
            />
          </label>
        {/if}
      {:else}
        <label class="field">
          <span class="field__label">
            {$tStore('views.procurements.filters.minValue', {
              currency: $tStore('views.procurements.filters.currencyAny'),
            })}
          </span>
          <input class="input" type="number" min="0" step="any" bind:value={filters.minValueEur} />
        </label>
        <label class="field">
          <span class="field__label">
            {$tStore('views.procurements.filters.maxValue', {
              currency: $tStore('views.procurements.filters.currencyAny'),
            })}
          </span>
          <input class="input" type="number" min="0" step="any" bind:value={filters.maxValueEur} />
        </label>
      {/if}

      <div class="filters__actions">
        <button type="submit" class="btn btn--primary">
          {$tStore('common.filter')}
        </button>
        <button type="button" class="btn btn--secondary" onclick={onClear}>
          {$tStore('common.clearFilters')}
        </button>
      </div>
    </form>
  {/if}
</div>

<style lang="scss">
  @use '../../../assets/styles/variables' as v;

  // Sticks below the app header (which is itself sticky at top: 0) so the
  // bulk-selection controls — and, when expanded, the whole filters form —
  // stay reachable while scrolling a long tender list. Wraps the head AND
  // the (optional) form together, not just the head: a sticky element only
  // stays pinned while its parent still has room below it, and the form
  // needs to float along with the head rather than being left behind in
  // normal flow. Bled out to the card's own edges (negative margin matching
  // the card's padding, then restored as this element's own padding) so the
  // stuck panel spans the full card width with an opaque background instead
  // of leaving the card's side padding transparent to whatever scrolls
  // underneath.
  .filter-panel {
    position: sticky;
    top: v.$header-height;
    z-index: 5;
    display: flex;
    flex-direction: column;
    gap: v.$space-md;
    background: v.$color-surface-elevated;
    margin: calc(-1 * #{v.$space-lg}) calc(-1 * #{v.$space-lg}) 0;
    padding: v.$space-lg v.$space-lg v.$space-sm;
    border-bottom: 1px solid v.$color-border;
    border-radius: v.$radius-lg v.$radius-lg 0 0;
  }

  .filter-panel__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: v.$space-sm;
    flex-wrap: wrap;

    h3 {
      margin: 0;
    }
  }

  .filter-panel__head-actions {
    display: flex;
    align-items: center;
    gap: v.$space-sm;
    flex-wrap: wrap;
  }

  // Live "N/max slots" readout — muted so it reads as status text, not an
  // action, distinguishing it from the buttons it sits next to.
  .bulk-slots {
    font-size: 0.8125rem;
    color: v.$color-text-muted;
    white-space: nowrap;
  }

  // Clickable variant — reopens the results panel without losing the
  // accumulated record, so closing it isn't a dead end while tenders from an
  // earlier batch are still running.
  .bulk-slots--link {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      color: v.$color-text;
    }
  }

  .filters__toggle {
    display: inline-flex;
    align-items: center;
    gap: v.$space-xs;
  }

  .filters__toggle-icon {
    font-size: 0.75rem;
    line-height: 1;
  }

  .filters {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: v.$space-md;
    align-items: end;

    @media (max-width: v.$bp-lg) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: v.$bp-sm) {
      grid-template-columns: 1fr;
    }
  }

  .filters__saved {
    grid-column: 1 / -1;
    max-width: 28rem;
  }

  .filters__saved-label {
    display: flex;
    align-items: center;
    gap: v.$space-xs;
  }

  // Locked "Source" field: the (disabled) Dropdown plus a small clear
  // button that hands control back to the free-choice dropdown below.
  .field__locked-source {
    display: flex;
    align-items: center;
    gap: v.$space-xs;

    :global(.dropdown) {
      flex: 1;
      min-width: 0;
    }
  }

  .field__clear-source {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    background: v.$color-surface-elevated;
    color: v.$color-text-muted;
    border: 1px solid v.$color-border-strong;
    border-radius: v.$radius-sm;
    font-size: 0.875rem;
    line-height: 1;
    cursor: pointer;

    &:hover {
      background: v.$color-surface;
      color: v.$color-text;
      border-color: v.$color-text-subtle;
    }

    &:focus-visible {
      outline: 2px solid v.$color-primary;
      outline-offset: 2px;
    }
  }

  .filters__search {
    grid-column: span 2;

    @media (max-width: v.$bp-sm) {
      grid-column: span 1;
    }
  }

  .filters__actions {
    display: flex;
    gap: v.$space-sm;
    grid-column: 1 / -1;
    justify-content: flex-end;
  }
</style>
